import React, { useEffect, useMemo, useState } from 'react';
import { FormControlLabel, MenuItem, Radio, RadioGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';

import AppTextField from '../../../designe/app-text-field/AppTextField';
import AppButton from '../../../designe/app-button/AppButton';
import { useGetMyCommunities } from '../../../../services/redux/api/communitiesApi';
import { useGetMyDetails } from '../../../../services/redux/api/usersApi';
import { useStyles } from './ReplyPost.styles';
import theme from '../../../../styles/theme';
import { ICommunity } from '../../../../types/interfaces/community';
import { IPost } from '../../../../types/interfaces/post';

enum OptionId {
  privateMessage = 'privateMessage',
  communityPost = 'communityPost',
  pagePost = 'pagePost',
}

interface IReplyPostProps {
  onClose: () => void;
  onSavePost: (post: IPost) => void;
}

export default function ReplyPost({ onClose, onSavePost }: IReplyPostProps) {
  const [newPostContent, setNewPostContent] = useState();
  const [loader, setLoader] = useState(false);
  const [selectedCommunity, setSelectedCommunity] = useState<ICommunity>(null);
  const [replayTo, setReplayTo] = useState<OptionId>(OptionId.pagePost);
  const { t, i18n } = useTranslation();
  const [userCommunities, setUserCommunities] = useState([]);
  const { data: user } = useGetMyDetails();
  const { data: communities } = useGetMyCommunities();
  const { classes } = useStyles(theme);

  useEffect(() => {
    if (!communities) return;

    const userCommunities = communities.filter((c) => c.ownerId === user.id);
    setSelectedCommunity(userCommunities[0]);
    setUserCommunities(userCommunities);
  }, [communities]);

  function getRadioComponent(option: OptionId, disabled: boolean) {
    return (
      <Radio
        disabled={disabled}
        classes={{ root: classes.optionItem, checked: classes.checked }}
        checked={replayTo === option}
        onChange={() => setReplayTo(option)}
      />
    );
  }

  function onSavePostClick() {
    setLoader(true);
    let promise;

    if (replayTo === OptionId.pagePost) {
      promise = onSavePost({ content: newPostContent, pageUserId: user.id });
    } else if (replayTo === OptionId.communityPost) {
      promise = onSavePost({
        content: newPostContent,
        communityId: selectedCommunity.id,
      });
    }

    promise.finally(() => {
      setLoader(false);
      onClose();
    });
  }

  return (
    <div className={classes.replyPostWrapper}>
      <RadioGroup defaultValue={OptionId.pagePost}>
        <FormControlLabel
          value={OptionId.privateMessage}
          control={getRadioComponent(OptionId.privateMessage, true)}
          label={t('post.privateMessage')}
        />
        <FormControlLabel
          value={OptionId.communityPost}
          control={getRadioComponent(
            OptionId.communityPost,
            !userCommunities?.length,
          )}
          label={t('post.communityPost')}
        />
        {replayTo === OptionId.communityPost && (
          <AppTextField
            label={t('common.community')}
            fullWidth
            value={selectedCommunity?.id}
            onChange={(e) =>
              setSelectedCommunity(
                userCommunities.find((uc) => uc.id === e.target.value),
              )
            }
            select>
            {userCommunities.map((community) => (
              <MenuItem
                className={classes.community}
                key={`community-${community.id}`}
                value={community.id}>
                {community.name}
              </MenuItem>
            ))}
          </AppTextField>
        )}

        <FormControlLabel
          value={OptionId.pagePost}
          control={getRadioComponent(OptionId.pagePost, false)}
          label={t('post.pagePost')}
        />
      </RadioGroup>

      <AppTextField
        label={t('post.yourMessage')}
        placeholder={t('post.yourMessage')}
        fullWidth
        multiline
        minRows={5}
        maxRows={5}
        value={newPostContent}
        onChange={(e) => setNewPostContent(e.target.value)}
      />
      <div className={classes.replyPostBottom}>
        <AppButton variant="outlined" onClick={onClose}>
          {t('common.cancel')}
        </AppButton>
        <AppButton onClick={onSavePostClick} loader={loader}>
          {t('common.save')}
        </AppButton>
      </div>
    </div>
  );
}
